import { useState } from 'react';
import {
  CheckCircle,
  XCircle,
  Clock,
  Settings,
  Trash2,
  AlertCircle
} from 'lucide-react';

export default function IntegrationCard({
  integration,
  logo,
  onConnect,
  onConfigure,
  onDisconnect
}) {
  const [isHovered, setIsHovered] = useState(false);

  const getStatusBadge = () => {
    switch (integration.status) {
      case 'connected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <CheckCircle className="w-3 h-3" />
            Connected
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
            <Clock className="w-3 h-3" />
            Pending Setup
          </span>
        );
      case 'error':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
            <AlertCircle className="w-3 h-3" />
            Error
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
            <XCircle className="w-3 h-3" />
            Not Connected
          </span>
        );
    }
  };

  const getIntegrationName = () => {
    switch (integration.type) {
      case 'ghl':
        return 'GoHighLevel';
      case 'hubspot':
        return 'HubSpot';
      case 'zapier':
        return 'Zapier';
      case 'salesforce':
        return 'Salesforce';
      default:
        return integration.type.toUpperCase();
    }
  };

  const getIntegrationDescription = () => {
    switch (integration.type) {
      case 'ghl':
        return 'Trigger AI calls from GHL workflows and sync results back to contacts';
      case 'hubspot':
        return 'Connect with HubSpot CRM, trigger calls from workflows, and log activities';
      case 'zapier':
        return 'Connect with 5,000+ apps through Zapier automation';
      case 'salesforce':
        return 'Enterprise CRM integration with Salesforce';
      default:
        return 'Connect this integration to Alfred Voice';
    }
  };

  const isComingSoon = integration.status === 'coming_soon';
  const isConnected = integration.status === 'connected';

  return (
    <div
      className={`relative bg-white rounded-xl border transition-all duration-200 ${
        isHovered && !isComingSoon ? 'shadow-lg border-gray-300' : 'shadow-sm border-gray-200'
      } ${isComingSoon ? 'opacity-60' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Coming Soon Badge */}
      {isComingSoon && (
        <div className="absolute top-3 right-3">
          <span className="px-2 py-1 bg-gray-800 text-white text-xs font-medium rounded-full">
            Coming Soon
          </span>
        </div>
      )}

      <div className="p-6">
        {/* Header */}
        <div className="flex items-start gap-4 mb-4">
          {logo}
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">
              {getIntegrationName()}
            </h3>
            {!isComingSoon && getStatusBadge()}
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-500 mb-4">
          {getIntegrationDescription()}
        </p>

        {/* Connected Info */}
        {isConnected && integration.metadata && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-xs text-gray-500 space-y-1">
              {integration.metadata.locationId && (
                <div>
                  <span className="font-medium">Location ID:</span>{' '}
                  {integration.metadata.locationId}
                </div>
              )}
              {integration.metadata.portalId && (
                <div>
                  <span className="font-medium">Portal ID:</span>{' '}
                  {integration.metadata.portalId}
                </div>
              )}
              {integration.connectedAt && (
                <div>
                  <span className="font-medium">Connected:</span>{' '}
                  {new Date(integration.connectedAt).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        {!isComingSoon && (
          <div className="flex items-center gap-2">
            {isConnected ? (
              <>
                <button
                  onClick={onConfigure}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm font-medium"
                >
                  <Settings className="w-4 h-4" />
                  Configure
                </button>
                <button
                  onClick={onDisconnect}
                  className="flex items-center justify-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium"
                  title="Disconnect"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            ) : (
              <button
                onClick={onConnect}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-black hover:bg-gray-800 text-white rounded-lg transition-colors text-sm font-medium"
              >
                Connect
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
